import { statusText } from './i18n';

export const ORDER_STATUSES = [
  'pending', 'pending_payment', 'flower_quote_pending', 'paid', 'confirmed',
  'assigned', 'accepted', 'worker_on_route', 'arrived', 'in_progress',
  'before_photos_uploaded', 'after_photos_uploaded',
  // Two-stage media flow: the customer service report is sent first, then the
  // separate internal admin proof is collected and submitted.
  'client_report_sent', 'admin_proof_pending',
  // Worker submitted; an admin must approve before the order can complete.
  'pending_admin_review', 'changes_requested',
  'completed', 'cancelled',
];

/** Statuses that need an admin decision right now. */
export const REVIEW_PENDING_STATUS = 'pending_admin_review';
export const CHANGES_REQUESTED_STATUS = 'changes_requested';

/** Human labels for the review lifecycle shown in the Orders table. */
export const STATUS_LABELS = {
  pending: 'Awaiting Payment',
  confirmed: 'Awaiting Worker',
  paid: 'Awaiting Worker',
  assigned: 'Awaiting Worker',
  accepted: 'In Progress',
  worker_on_route: 'In Progress',
  arrived: 'In Progress',
  in_progress: 'In Progress',
  before_photos_uploaded: 'In Progress',
  after_photos_uploaded: 'In Progress',
  client_report_sent: 'Customer Report Sent',
  admin_proof_pending: 'Collecting Admin Proof',
  pending_admin_review: 'Pending Admin Review',
  changes_requested: 'Changes Requested',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export function statusLabel(status, lang = 'en') {
  return statusText(lang, status);
}

export function countPendingReview(orders) {
  return (orders || []).filter(o => o.status === REVIEW_PENDING_STATUS).length;
}

export function statusAfterUnassign(order) {
  return order?.paymentStatus === 'paid' || order?.status === 'paid'
    ? 'confirmed'
    : 'pending';
}

/**
 * Whether an admin may assign a worker to this order right now.
 *
 * Mirrors the mobile worker's own self-claim/decline gate in
 * firebase/firestore.rules (`paymentStatus == 'paid'` required) -- that
 * rule only covers a WORKER claiming unassigned "new work" for themselves.
 * It does not, and structurally cannot without broadening unrelated admin
 * writes, cover an ADMIN manually assigning a worker through the panel,
 * since `allow write: if adminCan('orders')` there is deliberately
 * unconditional (every other admin order action -- status edits, archiving,
 * cancelling, flower quotes -- also needs it). This is the client-side
 * closing of that one specific gap: an unpaid order must never be handed
 * to a worker, whether the worker claims it themselves or an admin assigns
 * it directly (2026-10 payment-UX fix).
 *
 * @param {object} order
 * @return {{ok: boolean, reason: string}} `reason` is '' when `ok`.
 */
export function canAssignWorker(order) {
  if (String(order?.paymentStatus || '').toLowerCase() !== 'paid') {
    return {
      ok: false,
      reason: 'This order is not paid yet. A worker cannot be assigned until payment is confirmed.',
    };
  }
  return {ok: true, reason: ''};
}

export function visibleOrders(orders, showArchived = false) {
  return (orders || []).filter(order => Boolean(order.adminArchived) === showArchived);
}

export function assignmentAuditEvent(status, workerName) {
  return {
    status,
    en: status === 'assigned' ? `Assigned to ${workerName}` : 'Worker unassigned by admin',
    az: status === 'assigned' ? `${workerName} işçisinə təyin edildi` : 'İşçi admin tərəfindən silindi',
    ru: status === 'assigned' ? `Назначен работнику ${workerName}` : 'Работник снят администратором',
  };
}


/**
 * Human labels for the lifecycle events stored on `trackingEvents` and
 * `statusHistory`.
 *
 * These are DISPLAY labels only. The stored values stay exactly as the app and
 * the backend wrote them, so nothing downstream has to parse a translation.
 */
export const ACTIVITY_LABELS = {
  pending: 'Order placed',
  confirmed: 'Payment confirmed',
  paid: 'Payment confirmed',
  assigned: 'Worker assigned',
  accepted: 'Worker accepted',
  worker_on_route: 'Worker en route',
  arrived: 'Worker arrived',
  in_progress: 'Cleaning started',
  before_photos_uploaded: 'Before photos uploaded',
  after_photos_uploaded: 'After photos uploaded',
  customer_report_sent: 'Customer report sent',
  client_report_sent: 'Customer report sent',
  admin_proof_submitted: 'Admin proof submitted',
  pending_admin_review: 'Submitted for admin review',
  worker_submitted: 'Submitted for admin review',
  worker_resubmitted: 'Resubmitted for admin review',
  admin_approved: 'Admin approved',
  admin_rejected: 'Admin requested changes',
  changes_requested: 'Admin requested changes',
  completed: 'Service completed',
  cancelled: 'Order cancelled',
  location_changed: 'Grave location approved',
};

export function activityLabel(event, lang = 'en') {
  if (lang === 'en') return ACTIVITY_LABELS[event] || event || '—';
  return statusText(lang, event);
}

/**
 * The order's full lifecycle, oldest first: progress events the app wrote plus
 * review events the backend wrote, merged and sorted by their real timestamps.
 *
 * Nothing is invented — an order with no stored events produces an empty list.
 */
export function orderActivity(order) {
  const toMillis = value => {
    if (!value) return null;
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    return null;
  };

  const rows = [];
  for (const event of Array.isArray(order?.trackingEvents) ? order.trackingEvents : []) {
    if (!event) continue;
    rows.push({
      event: event.status || '',
      at: toMillis(event.time || event.at || event.createdAt),
      actorRole: '',
      actorId: '',
      reason: '',
      source: 'progress',
    });
  }
  for (const entry of Array.isArray(order?.statusHistory) ? order.statusHistory : []) {
    if (!entry) continue;
    rows.push({
      event: entry.event || '',
      at: toMillis(entry.createdAt || entry.at),
      actorRole: entry.actorRole || '',
      actorId: entry.actorId || '',
      reason: entry.reason || '',
      source: 'review',
    });
  }

  // Drop exact duplicates: submitting writes both a progress event and a
  // review event for the same moment.
  const seen = new Set();
  const unique = rows.filter(row => {
    const key = `${row.event}|${row.at || ''}|${row.source}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return unique.sort((a, b) => {
    if (a.at === null) return 1;
    if (b.at === null) return -1;
    return a.at - b.at;
  });
}
